import type { CmpConfig } from "../core/config";
import { getConsent } from "../core/consent";
import { getCachedConfig } from "../core/config";
import { getStyles } from "./styles";

const WIDGET_ID = "cmp-consent-widget";

let openHandler: (() => void) | null = null;

/** Set once at SDK init (reopen banner / preferences). */
export function setConsentWidgetOpenHandler(handler: () => void): void {
  openHandler = handler;
}

export function isConsentUiOpen(): boolean {
  return !!(
    document.getElementById("cmp-banner") ||
    document.getElementById("cmp-prefs")
  );
}

function widgetLabel(config: CmpConfig): string {
  const lang = (config.defaultLanguage || "tr").toLowerCase().startsWith("tr") ? "tr" : "en";
  // Language-strict: only use a translation whose language matches the user's; never
  // fall back to a different-language row. Localized default below covers the rest.
  const tr = config.translations.find(
    (t) => (t.language?.toLowerCase().startsWith("tr") ? "tr" : "en") === lang
  );
  const buttons = (tr?.buttonsJson || {}) as Record<string, string>;
  if (buttons.cookieSettingsText) return buttons.cookieSettingsText;
  return lang === "tr" ? "Çerez ayarları" : "Cookie settings";
}

export function isPrivacyWidgetEnabled(themeJson: unknown): boolean {
  const compliance = ((themeJson || {}) as { compliance?: { privacyWidgetVisible?: boolean } })
    .compliance;
  return compliance?.privacyWidgetVisible !== false;
}

/**
 * Show privacy widget only when consent exists and banner/preferences are closed.
 */
export function syncConsentWidget(config?: CmpConfig | null): void {
  const cfg = config ?? getCachedConfig();
  if (!cfg || !openHandler) {
    removeConsentWidget();
    return;
  }

  if (isConsentUiOpen() || !getConsent() || !isPrivacyWidgetEnabled(cfg.themeJson)) {
    removeConsentWidget();
    return;
  }

  if (document.getElementById(WIDGET_ID)) return;

  if (!document.getElementById("cmp-styles")) {
    const styleEl = document.createElement("style");
    styleEl.id = "cmp-styles";
    styleEl.textContent = getStyles(cfg.themeJson);
    document.head.appendChild(styleEl);
  }

  const btn = document.createElement("button");
  btn.id = WIDGET_ID;
  btn.type = "button";
  // Widget her zaman sol altta. Banner ile çakışma kontrolü gereksiz: widget yalnızca
  // banner kapalıyken ve onay verilmişken gösteriliyor (bkz. yukarıdaki koşul), banner
  // ise yalnızca onay öncesi görünüyor — ikisi aynı anda ekranda bulunmuyor.
  btn.className = "cmp-consent-widget";
  btn.setAttribute("aria-label", widgetLabel(cfg));
  btn.title = widgetLabel(cfg);
  btn.innerHTML = COOKIE_ICON_SVG;

  btn.addEventListener("click", (e) => {
    e.preventDefault();
    openHandler?.();
  });

  document.body.appendChild(btn);
}

export function removeConsentWidget(): void {
  document.getElementById(WIDGET_ID)?.remove();
}

/** @deprecated Use syncConsentWidget */
export function renderConsentWidget(config: CmpConfig, onOpen: () => void): void {
  setConsentWidgetOpenHandler(onOpen);
  syncConsentWidget(config);
}

const COOKIE_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1.17 14.8c-2.1 0-3.83-1.24-4.58-3h1.6c.55 1.16 1.74 1.97 3.1 1.97s2.55-.81 3.1-1.97h1.6c-.75 1.76-2.48 3-4.58 3-.52 0-.94-.42-.94-.94s.42-.94.94-.94.94.42.94.94zm5.17-6.3c-.69 0-1.25-.56-1.25-1.25S15.31 9 16 9s1.25.56 1.25 1.25S16.69 11.5 16 11.5zM8 11.5c-.69 0-1.25-.56-1.25-1.25S7.31 9 8 9s1.25.56 1.25 1.25S8.69 11.5 8 11.5zm4-4c-.69 0-1.25-.56-1.25-1.25S11.31 6 12 6s1.25.56 1.25 1.25S12.69 7.5 12 7.5z"/></svg>`;
